import * as Sentry from '@sentry/browser';
import { createContext, useContext, useEffect, useMemo } from 'react';

import { useMeQuery, UserFieldsFragment } from '@/@generated/codegen/API';
import { KeycloakAuthContext, useKeycloakAuth } from '@/auth/KeycloakProvider';
import { usePrevious } from '@/utils/helpers/react.helper';

type Props = {
  children: React.ReactNode;
};

export enum LoggedType {
  LOGGED_IN = 'logged-in',
  LOADING = 'logging-in-progress',
  LOGGED_OUT = 'logged-out',
}

export type LoggedOutContextType = {
  status: LoggedType.LOGGED_OUT;
};

export type LoadingContextType = {
  status: LoggedType.LOADING;
  isAdmin?: boolean;
  userID?: string;
  email?: string;
};

export type LoggedInContextType = {
  status: LoggedType.LOGGED_IN;
  isAdmin: boolean;
  userID: string;
  email: string;
  currentUser: UserFieldsFragment;
  isGuest: boolean;
};

export type UserAuthenticationContextType =
  | LoggedOutContextType
  | LoadingContextType
  | LoggedInContextType;

export const UserAuthenticationContext = createContext<UserAuthenticationContextType | undefined>(
  undefined
);

export function UserAuthenticationContextProvider({ children }: Props) {
  const tokenCtx = useKeycloakAuth();
  const previousTokenStatus = usePrevious(tokenCtx.tokenStatus);
  const previousTokenUserId = usePrevious(
    tokenCtx.tokenStatus === 'valid' ? tokenCtx.userID : null
  );
  const userQuery = useMeQuery({
    skip: tokenCtx.tokenStatus !== 'valid',
  });
  const previousUser: UserFieldsFragment | null | undefined = usePrevious(userQuery.data?.me);

  const contextValue: UserAuthenticationContextType = useMemo(() => {
    if (tokenCtx.tokenStatus === 'loading') {
      return { status: LoggedType.LOADING } satisfies LoadingContextType;
    }

    if (tokenCtx.tokenStatus === 'invalid' || userQuery.error) {
      if (userQuery.error) {
        console.error('[UserAuthCtx] me query error:', userQuery.error);
      }
      return { status: LoggedType.LOGGED_OUT } satisfies LoggedOutContextType;
    }

    const user = userQuery.data?.me || previousUser;
    if (!user) {
      return { status: LoggedType.LOADING, ...tokenCtx } satisfies LoadingContextType;
    }

    Sentry.setUser({ id: tokenCtx.userID, email: tokenCtx.email });

    return {
      status: LoggedType.LOGGED_IN,
      isGuest: user.isGuest,
      email: tokenCtx.email?.replace(/_\d+(?=@)/, '') ?? tokenCtx.email,
      currentUser: user,
      isAdmin: tokenCtx.isAdmin,
      userID: tokenCtx.userID,
    } satisfies LoggedInContextType;
  }, [tokenCtx.tokenStatus, userQuery.data?.me, userQuery.error, previousUser]);

  useEffect(() => {
    if (
      (previousTokenStatus === 'invalid' && tokenCtx.tokenStatus === 'valid') ||
      // NOTE: The user ID changed, refetch user data
      (tokenCtx.tokenStatus === 'valid' && previousTokenUserId !== tokenCtx.userID)
    ) {
      console.log('DEBUG >> UserAuthContext >> refetching user data');
      void userQuery.refetch();
    }
  }, [tokenCtx]);

  return (
    <UserAuthenticationContext.Provider value={contextValue}>
      {children}
    </UserAuthenticationContext.Provider>
  );
}

export const useLoggedInUserAuthContext = (): LoggedInContextType => {
  const tokenContext = useContext(KeycloakAuthContext);
  const userContext = useContext(UserAuthenticationContext);
  if (tokenContext === undefined) {
    throw new Error('useLoggedInUserAuthContext must be used within a KeycloakProvider');
  }

  if (tokenContext.tokenStatus !== 'valid') {
    throw new Error('useLoggedInUserAuthContext can only be used when having a valid token');
  }

  if (userContext === undefined) {
    throw new Error(
      'useLoggedInUserAuthContext must be used within a UserAuthenticationContextProvider'
    );
  }
  if (userContext.status !== LoggedType.LOGGED_IN) {
    throw new Error('useLoggedInUserAuthContext can only be used when user data is loaded');
  }
  return userContext;
};

export const useUserAuthContext = () => {
  const context = useContext(UserAuthenticationContext);
  if (context === undefined) {
    throw new Error('useUserAuthContext must be used within a UserAuthenticationContextProvider');
  }
  return context;
};
